import { useEffect, useMemo, useRef, useState } from 'react';
import clsx from 'clsx';
import { useApi } from '../api-context';

/**
 * The product, culling itself.
 *
 * "3,000 photos in 30 minutes" is a boast; watching real photographs go past at
 * culling speed is proof, and it costs the reader nothing to watch. This is the
 * argument the page makes before it asks for anything, so it is dressed as the
 * actual m3cull window — same counter, same keys, same filmstrip — rather than
 * as a slideshow.
 *
 * It stops when scrolled out of view, and never starts for anyone who has asked
 * for reduced motion.
 */

/** Roughly what a real cull looks like: more rejects than keeps. */
const PATTERN: ('keep' | 'reject')[] = [
  'keep', 'reject', 'reject', 'keep', 'reject', 'keep', 'reject',
  'reject', 'keep', 'reject', 'keep', 'reject', 'reject', 'keep',
];

const HOLD_MS = 1250;
const FLASH_MS = 300;

export default function HeroDemo() {
  const api = useApi();
  const [photos, setPhotos] = useState<string[]>([]);
  const [index, setIndex] = useState(0);
  const [flash, setFlash] = useState<'keep' | 'reject' | null>(null);
  const [running, setRunning] = useState(true);
  const frame = useRef<HTMLDivElement>(null);

  const reducedMotion = useMemo(
    () =>
      typeof window !== 'undefined' &&
      window.matchMedia?.('(prefers-reduced-motion: reduce)').matches,
    [],
  );

  useEffect(() => {
    let cancelled = false;
    void (async () => {
      const urls = await api.sampleShootUrls(PATTERN.length);
      if (cancelled) return;
      setPhotos(urls);
      urls.slice(0, 4).forEach((url) => {
        const img = new Image();
        img.src = url;
      });
    })();
    return () => {
      cancelled = true;
    };
  }, [api]);

  useEffect(() => {
    const element = frame.current;
    if (!element || reducedMotion) return;
    const observer = new IntersectionObserver(([e]) => setRunning(e.isIntersecting), {
      threshold: 0.2,
    });
    observer.observe(element);
    return () => observer.disconnect();
  }, [reducedMotion]);

  useEffect(() => {
    if (reducedMotion || !running || photos.length === 0) return;

    const decision = PATTERN[index % PATTERN.length];
    const timer = setTimeout(() => {
      setFlash(decision);
      setTimeout(() => setFlash(null), FLASH_MS);
      setIndex((i) => (i + 1) % photos.length);
    }, HOLD_MS);
    return () => clearTimeout(timer);
  }, [index, running, photos.length, reducedMotion]);

  const tally = useMemo(() => {
    let kept = 0;
    for (let i = 0; i < index; i++) if (PATTERN[i % PATTERN.length] === 'keep') kept++;
    return { kept, rejected: index - kept };
  }, [index]);

  const total = photos.length || PATTERN.length;
  const decidedAt = (position: number) =>
    position < index ? PATTERN[position % PATTERN.length] : null;

  return (
    <div
      ref={frame}
      className="overflow-hidden rounded-xl border border-ink-800 bg-ink-900 shadow-2xl shadow-black/60"
    >
      {/* ---- window chrome, matching the real review screen ---- */}
      <div className="flex items-center gap-3 border-b border-ink-850 px-3 py-2 sm:px-4">
        <div className="flex gap-1.5" aria-hidden="true">
          <span className="h-2.5 w-2.5 rounded-full bg-ink-700" />
          <span className="h-2.5 w-2.5 rounded-full bg-ink-700" />
          <span className="h-2.5 w-2.5 rounded-full bg-ink-700" />
        </div>

        <div className="min-w-0">
          <p className="truncate text-tiny font-medium text-ink-100">Marsh, Tuesday</p>
          <p className="tnum hidden truncate text-[10.5px] text-ink-500 sm:block">
            {index + 1} of {total} · _DSC{String(41 + index).padStart(4, '0')}.JPG
          </p>
        </div>

        <div className="ml-auto flex items-center gap-2.5">
          <span className="tnum hidden text-[10.5px] text-ink-500 sm:inline">
            <span className="text-ink-200">{index}</span> of {total} reviewed
          </span>
          <div className="h-[3px] w-14 overflow-hidden rounded-full bg-ink-800 sm:w-20">
            <div
              className="h-full rounded-full bg-ink-100 transition-[width] duration-500 ease-out"
              style={{ width: `${(index / total) * 100}%` }}
            />
          </div>
        </div>
      </div>

      {/* ---- the photograph ---- */}
      <div className="relative aspect-[3/2] w-full bg-ink-950">
        {photos.map((url, position) => (
          <img
            key={url}
            src={url}
            alt=""
            aria-hidden="true"
            className={clsx(
              'absolute inset-0 h-full w-full object-cover transition-opacity duration-500',
              position === index ? 'opacity-100' : 'opacity-0',
            )}
          />
        ))}
        {photos.length === 0 && <div className="checker absolute inset-0" />}

        <div
          className={clsx(
            'pointer-events-none absolute inset-0 transition-opacity duration-200',
            flash ? 'opacity-100' : 'opacity-0',
          )}
        >
          <div
            className={clsx(
              'absolute inset-0 border-[3px]',
              flash === 'keep' ? 'border-keep' : 'border-reject',
            )}
          />
          <div
            className={clsx(
              'absolute left-4 top-4 rounded border px-3 py-1.5 text-caption font-medium backdrop-blur-sm',
              flash === 'keep'
                ? 'border-keep-line bg-keep-soft/90 text-keep'
                : 'border-reject-line bg-reject-soft/90 text-reject',
            )}
          >
            {flash === 'keep' ? 'Keep' : 'Reject'}
          </div>
        </div>
      </div>

      {/* ---- bucket bar ---- */}
      <div className="flex items-center gap-2 border-t border-ink-850 px-3 py-2.5 sm:px-4">
        <KeyCap digit="1" label="Keep" tone="keep" count={tally.kept} lit={flash === 'keep'} />
        <KeyCap
          digit="2"
          label="Reject"
          tone="reject"
          count={tally.rejected}
          lit={flash === 'reject'}
        />
        <span className="ml-auto hidden text-[10.5px] text-ink-600 lg:inline">
          {reducedMotion ? 'Two keys per photo.' : 'Running by itself. This is culling speed.'}
        </span>
      </div>

      {/* ---- filmstrip ---- */}
      <div className="flex gap-1 border-t border-ink-850 bg-ink-950/60 p-2" aria-hidden="true">
        {(photos.length ? photos : Array.from({ length: PATTERN.length }, () => '')).map(
          (url, position) => {
            const decision = decidedAt(position);
            return (
              <div
                key={url || position}
                className={clsx(
                  'relative h-8 flex-1 overflow-hidden rounded-[3px] transition-all duration-300 sm:h-11',
                  position === index ? 'ring-2 ring-ink-50' : 'opacity-50',
                )}
              >
                {url ? (
                  <img src={url} alt="" className="h-full w-full object-cover" />
                ) : (
                  <div className="h-full w-full bg-ink-800" />
                )}
                {decision && (
                  <span
                    className={clsx(
                      'absolute inset-x-0 bottom-0 h-[3px]',
                      decision === 'keep' ? 'bg-keep' : 'bg-reject',
                    )}
                  />
                )}
              </div>
            );
          },
        )}
      </div>
    </div>
  );
}

function KeyCap({
  digit,
  label,
  tone,
  count,
  lit,
}: {
  digit: string;
  label: string;
  tone: 'keep' | 'reject';
  count: number;
  lit: boolean;
}) {
  return (
    <span
      className={clsx(
        'flex items-center gap-2 rounded border px-2.5 py-1.5 text-caption transition-all duration-150',
        lit && tone === 'keep' && 'scale-[1.04] border-keep bg-keep-soft text-keep',
        lit && tone === 'reject' && 'scale-[1.04] border-reject bg-reject-soft text-reject',
        !lit && 'border-ink-700 text-ink-300',
      )}
    >
      <kbd
        className={clsx(
          'flex h-[18px] w-[18px] items-center justify-center rounded-[3px] border font-mono text-[10.5px]',
          lit ? 'border-current' : 'border-ink-600 bg-ink-800',
        )}
      >
        {digit}
      </kbd>
      <span className="hidden sm:inline">{label}</span>
      <span className="tnum text-ink-500">{count}</span>
    </span>
  );
}
